/** How a formatted note is stored, and how it is rendered.
 *
 * Notes are PLAIN TEXT carrying three markers:
 *
 *     **bold**       *italic*       ==highlight==
 *
 * The learner never sees them — RichTextEditor shows real bold and real
 * highlighting and converts at the edges. The markers are a storage format,
 * not an interface, which was the client's complaint about the old editor.
 *
 * Why not store HTML, now that the editor is a rich one? Because storing
 * HTML a user produced means sanitising it forever, and every paste is a
 * fresh opportunity to get that wrong. Keeping the stored form to three
 * markers means the dangerous case cannot arise: rendering builds React
 * elements from a parse, there is no dangerouslySetInnerHTML anywhere, and
 * anything a learner pastes that we do not understand becomes plain text on
 * the way in. It also means every note written before the editor existed
 * still reads correctly, with no migration.
 */
import type { ReactNode } from "react";

export type PieceKind = "text" | "bold" | "italic" | "mark";
export interface Piece {
  kind: PieceKind;
  text: string;
}

/** Split a note into plain, bold, italic and highlighted pieces, in order.
 *
 *  `**` is tried before `*`, or every bold run would parse as an italic one
 *  wrapping a stray asterisk. */
export function parseNote(source: string): Piece[] {
  const pieces: Piece[] = [];
  // Non-greedy so `**a** and **b**` is two bold runs, not one long one.
  const pattern = /(\*\*(.+?)\*\*|==(.+?)==|\*(.+?)\*)/gs;
  let at = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(source)) !== null) {
    if (m.index > at) pieces.push({ kind: "text", text: source.slice(at, m.index) });
    if (m[2] !== undefined) pieces.push({ kind: "bold", text: m[2] });
    else if (m[3] !== undefined) pieces.push({ kind: "mark", text: m[3] });
    else pieces.push({ kind: "italic", text: m[4] });
    at = m.index + m[0].length;
  }
  if (at < source.length) pieces.push({ kind: "text", text: source.slice(at) });
  return pieces;
}

/** Is there any formatting in here worth previewing? Used to keep the
 *  editor's preview out of the way of an ordinary, unformatted note. */
export function hasMarkup(source: string): boolean {
  return parseNote(source).some((p) => p.kind !== "text");
}

export function RichNote({ content }: { content: string }) {
  return (
    <p className="whitespace-pre-wrap text-sm text-ink">
      {parseNote(content).map((piece, i) => renderPiece(piece, i))}
    </p>
  );
}

function renderPiece(piece: Piece, key: number): ReactNode {
  if (piece.kind === "bold") return <strong key={key}>{piece.text}</strong>;
  if (piece.kind === "italic") return <em key={key}>{piece.text}</em>;
  if (piece.kind === "mark") {
    return (
      <mark key={key} className="rounded-sm bg-warning/25 px-0.5 text-ink">
        {piece.text}
      </mark>
    );
  }
  return <span key={key}>{piece.text}</span>;
}

/** Wrap the current selection in `marker`, or insert an empty pair with the
 *  caret between them when nothing is selected. Returns the new value and
 *  where the caret should land. */
export interface MarkerResult {
  value: string;
  /** Where the selection should sit afterwards. The marked words stay
   *  selected, so pressing the button again plainly UNDOES it — which is
   *  what every editor does, and what stops a second press nesting. */
  selectionStart: number;
  selectionEnd: number;
}

export function applyMarker(
  value: string,
  start: number,
  end: number,
  marker: string,
): MarkerResult {
  const selected = value.slice(start, end);
  const before = value.slice(0, start);
  const after = value.slice(end);
  const len = marker.length;

  // --- already marked? then unmark -----------------------------------
  //
  // This is the half that was missing. `applyMarker` only ever wrapped, and
  // it collapsed the caret afterwards — but a phone keeps the selection
  // visible across a toolbar tap, so a learner pressing B twice on the same
  // words got `****bbv****`, and three times `******bbv******`, which the
  // renderer then read as a bold asterisk. The client's screenshot exactly.

  // The markers are inside the selection: "**bbv**" is highlighted.
  if (
    selected.length >= len * 2 &&
    selected.startsWith(marker) &&
    selected.endsWith(marker)
  ) {
    const inner = selected.slice(len, selected.length - len);
    return {
      value: before + inner + after,
      selectionStart: start,
      selectionEnd: start + inner.length,
    };
  }

  // The markers are just outside it: "**" + [bbv] + "**".
  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      value: before.slice(0, before.length - len) + selected + after.slice(len),
      selectionStart: start - len,
      selectionEnd: start - len + selected.length,
    };
  }

  // --- otherwise, mark it --------------------------------------------
  const next = `${before}${marker}${selected}${marker}${after}`;
  return {
    value: next,
    // Nothing selected: sit between the markers, ready to type. Something
    // selected: keep it selected, so the button now reads as "on" and a
    // second press turns it off.
    selectionStart: start + len,
    selectionEnd: start + len + selected.length,
  };
}
